import { Injectable, PLATFORM_ID, inject, DOCUMENT } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { StoreRegionService } from './store-region.service';
import { StoreCountry } from '../models/location.model';
import { filter, distinctUntilChanged, map } from 'rxjs/operators';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class RegionRoutingService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private storeRegionService = inject(StoreRegionService);
  private platformId = inject(PLATFORM_ID);
  private document = inject<Document>(DOCUMENT);

  private currentUrlSubject = new BehaviorSubject<string>('');
  private previousRegionSubject = new BehaviorSubject<StoreCountry | null>(
    null
  );
  private isBrowser: boolean;
  private isHandlingRegionChange = false;
  private static readonly REGION_HANDLING_KEY = 'regionRoutingHandling';

  constructor() {
    const platformId = this.platformId;

    this.isBrowser = isPlatformBrowser(platformId);
    this.initializeService();
  }

  private setRegionHandlingFlag(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(
          RegionRoutingService.REGION_HANDLING_KEY,
          'true'
        );
      } catch (e) {
        // console.warn(
        //   'Failed to set region handling flag in sessionStorage:',
        //   e
        // );
      }
    }
  }

  private clearRegionHandlingFlag(): void {
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.removeItem(RegionRoutingService.REGION_HANDLING_KEY);
      } catch (e) {
        // console.warn(
        //   'Failed to clear region handling flag from sessionStorage:',
        //   e
        // );
      }
    }
  }

  private getRegionHandlingFlag(): boolean {
    if (isPlatformBrowser(this.platformId)) {
      try {
        return (
          sessionStorage.getItem(RegionRoutingService.REGION_HANDLING_KEY) ===
          'true'
        );
      } catch (e) {
        // console.warn(
        //   'Failed to read region handling flag from sessionStorage:',
        //   e
        // );
        return false;
      }
    }
    return false;
  }

  private initializeService(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => event.urlAfterRedirects)
      )
      .subscribe((url) => {
        this.currentUrlSubject.next(url);
      });

    this.currentUrlSubject.next(this.router.url);

    this.setupRegionChangeHandler();
  }

  private setupRegionChangeHandler(): void {
    this.storeRegionService.selectedRegion$
      .pipe(distinctUntilChanged((prev, curr) => prev?.id === curr?.id))
      .subscribe((currentRegion) => {
        const previousRegion = this.previousRegionSubject.value;
        if (
          previousRegion &&
          currentRegion &&
          previousRegion.id !== currentRegion.id
        ) {
          this.handleRegionChange(currentRegion, previousRegion);
        }
        this.previousRegionSubject.next(currentRegion);
      });
  }

  public handleRegionChange(
    newRegion: StoreCountry,
    previousRegion: StoreCountry
  ): void {
    this.isHandlingRegionChange = true;
    this.setRegionHandlingFlag();
    const currentUrl = this.currentUrlSubject.value;
    if (this.isCategoryUrl(currentUrl)) {
      this.handleCategoryNavigation(newRegion);
    } else if (this.isHomepageUrl(currentUrl)) {
      this.handleHomepageNavigation(newRegion);
    } else {
      this.handleGeneralNavigation();
    }
  }

  private isCategoryUrl(url: string): boolean {
    const urlSegments = url
      .split('?')[0]
      .split('/')
      .filter((segment) => segment !== '');
    return urlSegments.length >= 2 && urlSegments[0] === 'category';
  }

  private isHomepageUrl(url: string): boolean {
    let [path, queryString] = url.split('?');
    if (path.length > 1 && path.endsWith('/')) {
      path = path.slice(0, -1); // Remove trailing slash
    }
    const urlSegments = path.split('/').filter((segment) => segment !== '');
    const countryList = this.storeRegionService.countryList;
    if (urlSegments.length === 0) {
      return true;
    }

    if (
      urlSegments.length === 1 &&
      countryList?.some(
        (country) => country.code.toLowerCase() === urlSegments[0].toLowerCase()
      )
    ) {
      return true;
    }

    if (urlSegments.length === 0 && queryString) {
      const urlParams = new URLSearchParams(queryString);
      const regionParam = urlParams.get('region') || urlParams.get('country');
      if (
        regionParam &&
        countryList?.some(
          (country) => country.code.toLowerCase() === regionParam.toLowerCase()
        )
      ) {
        return true;
      }
    }

    return false;
  }

  private handleCategoryNavigation(newRegion: StoreCountry): void {
    const currentUrl = this.currentUrlSubject.value;
    const urlSegments = currentUrl
      .split('?')[0]
      .split('/')
      .filter((segment) => segment !== '');
    if (urlSegments[0] === 'category') {
      const currentCountryCode = urlSegments[1];
      const hasAll = urlSegments[2].toLowerCase() === 'all';
      const categoryId = urlSegments.length >=3 && urlSegments[2].toLowerCase() !== 'all' ? urlSegments[2] : '';
      const categoryName = urlSegments.length == 4 ? urlSegments[3] : '';

      let newPath: string[];
      if(hasAll) {
        newPath = ['category', newRegion.code, 'all'];
      } else if (categoryId && categoryName) {
        newPath = ['category', newRegion.code, categoryId, categoryName];
      } else {
        newPath = ['category', newRegion.code];
      }

      const queryString = currentUrl.split('?')[1];
      const navigationExtras: any = {};

      if (queryString) {
        const urlParams = new URLSearchParams(queryString);
        const queryParams: any = {};

        urlParams.forEach((value, key) => {
          queryParams[key] = value;
        });
      }

      this.router.navigate(newPath, navigationExtras);
    } else {
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { country: newRegion.code },
        queryParamsHandling: 'merge'
      });
    }
  }

  private handleHomepageNavigation(newRegion: StoreCountry): void {
    if (newRegion.has_homepage) {
      if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
        let currentPath = this.currentUrlSubject.value.split('?')[0];
        if (currentPath.length > 1 && currentPath.endsWith('/')) {
          currentPath = currentPath.slice(0, -1);
        }
        const targetPath = `/${newRegion.code}`;
        if (currentPath !== targetPath) {
          window.location.href = targetPath;
        }
      } else {
        this.router.navigate(['/', newRegion.code], {
          queryParams: {},
          queryParamsHandling: 'merge'
        });
      }
    } else {
      const targetUrl = `/?region=${newRegion.code}`;
      if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
        const currentParams = new URLSearchParams(window.location.search);
        if (currentParams.get('region') !== newRegion.code) {
          window.location.href = targetUrl;
          return;
        }
      } else {
        this.router.navigate(['/'], {
          queryParams: { region: newRegion.code }
        });
      }
    }
  }

  redirectTo(uri: string) {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([uri]);
    });
  }

  private handleGeneralNavigation(): void {
    if (isPlatformBrowser(this.platformId) && typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  public getCurrentUrl$() {
    return this.currentUrlSubject.asObservable();
  }

  public isHandlingRegionNavigation(): boolean {
    // Check both in-memory flag and sessionStorage for page reload scenarios
    // In SSR, sessionStorage is not available, so only check in-memory flag
    const sessionFlag = this.getRegionHandlingFlag();
    return this.isHandlingRegionChange || sessionFlag;
  }

  public endHandlingRegionNavigation(): void {
    this.isHandlingRegionChange = false;
    // Clear sessionStorage flag (browser only)
    this.clearRegionHandlingFlag();
  }

  public isCurrentUrlHomepage(): boolean {
    return this.isHomepageUrl(this.currentUrlSubject.value);
  }

  public navigateToHomepage(region: StoreCountry): void {
    this.handleHomepageNavigation(region);
  }

  public getHomepageUrl(region: StoreCountry): string {
    if (region.has_homepage) {
      return `/${region.code}`;
    } else {
      return `/?region=${region.code}`;
    }
  }

  public isCategoryUrlWithCountryCode(url: string): boolean {
    const urlSegments = url
      .split('?')[0]
      .split('/')
      .filter((segment) => segment !== '');
    const countryList = this.storeRegionService.countryList;

    return (
      urlSegments.length >= 2 &&
      urlSegments[0] === 'category' &&
      countryList?.some(
        (country) => country.code.toLowerCase() === urlSegments[1].toLowerCase()
      )
    );
  }

  public extractCountryCodeFromCategoryUrl(url: string): string | null {
    const urlSegments = url
      .split('?')[0]
      .split('/')
      .filter((segment) => segment !== '');

    if (urlSegments.length >= 2 && urlSegments[0] === 'category') {
      return urlSegments[1];
    }

    return null;
  }

  public buildCategoryUrl(currentUrl: string, newCountryCode: string): string {
    const [path, queryString] = currentUrl.split('?');
    const urlSegments = path.split('/').filter((segment) => segment !== '');

    if (urlSegments.length >= 2 && urlSegments[0] === 'category') {
      urlSegments[1] = newCountryCode;

      const newPath = '/' + urlSegments.join('/');
      return queryString ? `${newPath}?${queryString}` : newPath;
    }

    return currentUrl;
  }

  public isLegacyCategoryUrl(url: string): boolean {
    const urlSegments = url
      .split('?')[0]
      .split('/')
      .filter((segment) => segment !== '');
    const countryList = this.storeRegionService.countryList;

    if (urlSegments.length === 2 && urlSegments[0] === 'category') {
      const secondSegment = urlSegments[1];
      return !countryList?.some(
        (country) => country.code.toLowerCase() === secondSegment.toLowerCase()
      );
    }

    return false;
  }

  public convertLegacyCategoryUrl(url: string, countryCode: string): string {
    const [path, queryString] = url.split('?');
    const urlSegments = path.split('/').filter((segment) => segment !== '');

    if (urlSegments.length === 2 && urlSegments[0] === 'category') {
      const categoryName = urlSegments[1];
      const newPath = `/category/${countryCode}/${categoryName}`;
      return queryString ? `${newPath}?${queryString}` : newPath;
    }

    return url;
  }
}
